import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, HeartHandshake, Sparkles, MessagesSquare, ArrowRight, Quote } from "lucide-react";

const IMG = `${process.env.PUBLIC_URL}/community-babies.jpg`;

const benefits = [
  { icon: <Users size={20} />, title: "Meet like-minded families", text: "Connect with other local parents at the same stage of the journey — friendships often start right here at the poolside." },
  { icon: <Sparkles size={20} />, title: "Babies socialise early", text: "Little ones learn to share space, take turns and play alongside others — confidence that reaches far beyond the water." },
  { icon: <HeartHandshake size={20} />, title: "A support network", text: "New-parent life is easier together. Swap stories, share the wins and the wobbles, and feel part of something." },
  { icon: <MessagesSquare size={20} />, title: "Relaxed social swims", text: "Our Little Tiddler Social and Adult Social Swim sessions are all about connection — no lesson, just splashing and chatting." },
];

export default function CommunityBenefits() {
  return (
    <section className="pb-24 md:pb-32" data-testid="community-benefits">
      <div className="container mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0A2540]">
          <div className="blob absolute -top-28 -left-24 w-96 h-96 rounded-full bg-[#14B8A6]/25" />
          <div className="blob absolute -bottom-28 -right-24 w-96 h-96 rounded-full bg-[#8B5CF6]/25" />

          <div className="relative grid lg:grid-cols-2 gap-0 items-stretch">
            {/* Image side with floating card */}
            <div className="relative min-h-[360px] lg:min-h-full order-1 lg:order-none">
              <img
                src={IMG}
                alt="Parents and babies laughing together at a Happy Tiddlers swim session"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/10 to-transparent lg:bg-gradient-to-l lg:from-[#0A2540]/80 lg:via-transparent lg:to-transparent" />

              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="absolute bottom-6 left-6 right-6 lg:right-auto lg:max-w-xs bg-white/95 backdrop-blur-xl rounded-[1.75rem] p-6 shadow-2xl"
                data-testid="community-quote"
              >
                <Quote size={26} className="text-[#14B8A6]" />
                <p className="mt-3 font-heading text-lg font-semibold text-[#0F172A] leading-snug">
                  "We came for swimming and left with a whole group of friends."
                </p>
                <p className="mt-3 text-sm font-semibold text-[#64748B]">— A Happy Tiddlers parent</p>
              </motion.div>
            </div>

            {/* Text side */}
            <div className="p-9 md:p-14 lg:p-16 flex flex-col justify-center">
              <motion.span
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 text-sm text-[#5EEAD4] tracking-[0.25em] uppercase font-bold"
              >
                <Users size={16} /> More than swimming
              </motion.span>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.05]"
              >
                Friendships that grow{" "}
                <span className="text-[#FF7A1A]">beyond the pool</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.12 }}
                className="mt-6 text-lg text-[#CBD5E1] leading-relaxed max-w-xl"
              >
                Some of the strongest bonds we see aren't in the water — they're on the poolside.
                Our Baby &amp; Pre-School classes and social swims bring families together, week after
                week, and lasting friendships grow from there.
              </motion.p>

              <div className="mt-9 grid sm:grid-cols-2 gap-4">
                {benefits.map((b, i) => (
                  <motion.div
                    key={b.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: 0.12 + i * 0.08 }}
                    className="group rounded-2xl bg-white/[0.06] border border-white/10 p-5 hover:bg-white/[0.1] hover:border-[#5EEAD4]/30 transition-all duration-300"
                    data-testid={`community-benefit-${i}`}
                  >
                    <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#14B8A6] text-white mb-3 group-hover:scale-110 transition-transform">
                      {b.icon}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-white leading-snug">{b.title}</h3>
                    <p className="mt-1.5 text-sm text-[#94A3B8] leading-relaxed">{b.text}</p>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/timetable"
                className="inline-flex items-center gap-2 mt-9 self-start bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
                data-testid="community-cta"
              >
                Find a social swim <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
